import React from "react";

import moment from "moment";

import { DATE_FORMAT } from "@/src/constants/dateFormat";
import prettyDurations from "@/src/utils/durations";

import DataTableColumnHeader from "./DataTableColumnHeader";
import DataTableCountCell from "./DataTableCountCell";
import DataTableDragHandle from "./DataTableDragHandle";
import DataTableRowActions from "./DataTableRowActions";
import DataTableSelectCell from "./DataTableSelectCell";
import DataTableSelectHeader from "./DataTableSelectHeader";
import DataTableSrOnlyHeader from "./DataTableSrOnlyHeader";
import DataTableStatusCell from "./DataTableStatusCell";
import DataTableStatusDot from "./DataTableStatusDot";

import type { AccessorFnColumnDef, RowData } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import type { JSX, ReactNode } from "react";

import type { DURATION_UNITS } from "@/src/utils/durations";

import type {
  ColumnId,
  ColumnLayout,
  DataTableColumnDef,
  DataTableFeatures,
  RowAction,
  StatusOption,
} from "./dataTable.types";

const DATE_TIME_FORMAT = "DD/MM/YYYY HH:mm";
const DEFAULT_ACTIONS_LAYOUT: ColumnLayout = { width: "55px", align: "right" };
const DEFAULT_DATE_LAYOUT: ColumnLayout = {
  width: "12%",
  minWidth: 130,
  align: "center",
};
const DEFAULT_DATE_TIME_LAYOUT: ColumnLayout = {
  width: "13%",
  minWidth: 155,
  align: "center",
};
const DEFAULT_COUNT_LAYOUT: ColumnLayout = {
  width: "10%",
  minWidth: 110,
  align: "center",
};
const DEFAULT_DRAG_LAYOUT: ColumnLayout = { width: "45px" };
const DEFAULT_SELECT_LAYOUT: ColumnLayout = { width: "45px" };
const DEFAULT_STATUS_LAYOUT: ColumnLayout = { width: "75px", align: "center" };

// TanStack ne range en dernier, dans les deux sens, que les valeurs undefined : une date vide ou invalide doit en être une.
function toTimestamp(value: unknown): number | undefined {
  if (
    typeof value !== "string" &&
    typeof value !== "number" &&
    !(value instanceof Date)
  ) {
    return undefined;
  }
  const date = value === "" ? null : moment(value);
  return date?.isValid() ? date.valueOf() : undefined;
}

export function chronologicalColumn<TData extends RowData>(
  id: string,
  getDate: (row: TData) => unknown,
): AccessorFnColumnDef<DataTableFeatures, TData> & { id: string } {
  return {
    id,
    accessorFn: (row) => toTimestamp(getDate(row)),
    sortUndefined: "last",
  };
}

function rowActionsColumn<TData extends RowData>(
  renderActions: (row: TData) => JSX.Element,
  layout?: ColumnLayout,
): DataTableColumnDef<TData> {
  return {
    id: "actions",
    header: () => <DataTableSrOnlyHeader labelKey="dataTable.actions" />,
    enableSorting: false,
    meta: { ...DEFAULT_ACTIONS_LAYOUT, ...layout },
    cell: ({ row }) => renderActions(row.original),
  };
}

export function actionsColumn<TData extends RowData>(
  getActions: (row: TData) => RowAction[],
  getRowLabel: (row: TData) => string,
  layout?: ColumnLayout,
): DataTableColumnDef<TData> {
  return rowActionsColumn(
    (row) => (
      <DataTableRowActions
        actions={getActions(row)}
        rowLabel={getRowLabel(row)}
      />
    ),
    layout,
  );
}

export function lazyActionsColumn<TData extends RowData>(
  loadActions: (row: TData) => Promise<RowAction[]>,
  getRowLabel: (row: TData) => string,
  layout?: ColumnLayout,
): DataTableColumnDef<TData> {
  return rowActionsColumn(
    (row) => (
      <DataTableRowActions
        loadActions={async () => loadActions(row)}
        rowLabel={getRowLabel(row)}
      />
    ),
    layout,
  );
}

export function dateColumn<TData extends RowData>(
  key: ColumnId<TData>,
  header: string,
  layout?: ColumnLayout,
): DataTableColumnDef<TData> {
  return {
    ...chronologicalColumn<TData>(key, (row) => row[key]),
    meta: { ...DEFAULT_DATE_LAYOUT, ...layout, label: header },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={header} />
    ),
    cell: ({ getValue }) => {
      const timestamp = getValue<number | undefined>();
      return timestamp === undefined
        ? null
        : moment(timestamp).format(DATE_FORMAT);
    },
  };
}

export function dateTimeColumn<TData extends RowData>(
  key: ColumnId<TData>,
  header: string,
  layout?: ColumnLayout,
): DataTableColumnDef<TData> {
  return {
    ...dateColumn<TData>(key, header),
    meta: { ...DEFAULT_DATE_TIME_LAYOUT, ...layout, label: header },
    cell: ({ getValue }) => {
      const timestamp = getValue<number | undefined>();
      return timestamp === undefined
        ? null
        : moment(timestamp).format(DATE_TIME_FORMAT);
    },
  };
}

export function countColumn<TData extends RowData>(
  key: ColumnId<TData>,
  header: string,
  icon?: ReactNode,
  layout?: ColumnLayout,
): DataTableColumnDef<TData> {
  return {
    id: key,
    accessorFn: (row) => row[key],
    meta: { ...DEFAULT_COUNT_LAYOUT, ...layout, label: header },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={header} />
    ),
    // 0 ou absent : cellule vide, le tableau y met son tiret.
    cell: ({ getValue }) => {
      const count = Number(getValue());
      return count ? <DataTableCountCell count={count} icon={icon} /> : null;
    },
  };
}

/** Le tri porte sur la valeur brute, pas sur le texte affiché. */
export function durationColumn<TData extends RowData>(
  key: ColumnId<TData>,
  header: string,
  valueUnit: (typeof DURATION_UNITS)[keyof typeof DURATION_UNITS],
  format: "fastDuration" | "humanised",
  layout?: ColumnLayout,
): DataTableColumnDef<TData> {
  return {
    id: key,
    accessorFn: (row) => row[key],
    meta: { ...layout, label: header },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={header} />
    ),
    cell: ({ getValue }) =>
      prettyDurations(getValue<number>(), { valueUnit })[format],
  };
}

export function enabledStatusOptions(t: TFunction): StatusOption[] {
  return [
    { value: "enabled", label: t("table:status.enabled"), tone: "success" },
    { value: "disabled", label: t("table:status.disabled"), tone: "error" },
  ];
}

export function activityStatusOptions(t: TFunction): StatusOption[] {
  return [
    { value: "active", label: t("common:active"), tone: "success" },
    { value: "pause", label: t("common:pause"), tone: "warning" },
    { value: "stop", label: t("common:stop"), tone: "error" },
  ];
}

/** Sans tri : avec peu de valeurs, le filtre à facettes couvre le besoin. */
export function statusColumn<TData extends RowData>(
  key: ColumnId<TData>,
  header: string,
  options: StatusOption[],
  layout?: ColumnLayout,
  getTooltip?: (row: TData) => string[] | undefined,
): DataTableColumnDef<TData> {
  return {
    id: key,
    accessorFn: (row) => row[key],
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={header} />
    ),
    enableSorting: false,
    meta: {
      ...DEFAULT_STATUS_LAYOUT,
      ...layout,
      label: header,
      // La pastille sert de légende des couleurs dans le filtre.
      filterOptions: options.map(({ value, label, tone }) => ({
        value,
        label,
        icon: <DataTableStatusDot tone={tone} />,
      })),
    },
    cell: ({ getValue, row }) => {
      const currentValue = getValue();
      const option = options.find(
        (statusOption) => statusOption.value === currentValue,
      );

      return option ? (
        <DataTableStatusCell
          label={option.label}
          tone={option.tone}
          tooltip={getTooltip?.(row.original)}
        />
      ) : null;
    },
  };
}

export function dragHandleColumn<TData extends RowData>(
  getRowLabel: (row: TData) => string,
  layout?: ColumnLayout,
): DataTableColumnDef<TData> {
  return {
    id: "drag",
    header: () => <DataTableSrOnlyHeader labelKey="dataTable.reorder" />,
    enableSorting: false,
    meta: { ...DEFAULT_DRAG_LAYOUT, ...layout },
    cell: ({ row }) => (
      <DataTableDragHandle rowLabel={getRowLabel(row.original)} />
    ),
  };
}

export function selectColumn<TData extends RowData>(
  getRowLabel: (row: TData) => string,
  layout?: ColumnLayout,
): DataTableColumnDef<TData> {
  return {
    id: "select",
    header: ({ table }) => <DataTableSelectHeader table={table} />,
    enableSorting: false,
    enableHiding: false,
    meta: { ...DEFAULT_SELECT_LAYOUT, ...layout },
    cell: ({ row }) => (
      <DataTableSelectCell row={row} rowLabel={getRowLabel(row.original)} />
    ),
  };
}
